import React, { useState, useEffect } from "react";
import CkEditorField from "CommonComponent/CkEditorField/CkEditorField";
import { PageHeader, FormFieldLabel, CustomDropdownContainer } from "CommonComponent";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Form } from "react-bootstrap";
import Select from "react-select";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { flushSync } from "react-dom";
import validate from "validate.js";
import { CampaignDropdown, DesignationDropdown, OfficeDropdown, AppraisalSectionDropdown } from "CommonDropdowns";
import {
  createOrUpdateAppraisal,
  hideAppraisalsValidationError,
  resetAppraisalSetupFlow,
  toggleAppraisalSetupCategoryWindow,
  updateAppraisalSetupPublishStatus,
} from "actions";
import { isEmpty, isEqual } from "lodash";
import SetupCaregory from "../SetupCategory";
import { PublishStatusToggle, PUBLISH_STATUS } from "./component";
import { F, getAppraisalSetupSectionPivotRows } from "pages/AppraisalManagment/constants/legacyApiKeys";
import {
  FALLBACK_APPRAISAL_TYPE_OPTIONS,
} from "pages/Settings/PafDesignation/constants";
import { fetchAppraisalTypeOptions } from "pages/Settings/PafDesignation/appraisalTypesApi";
import "../AppraisalSetup.css";

const APPRAISAL_FREQUENCY_OPTIONS = [
  { value: "Monthly", label: "Monthly" },
  { value: "Quarterly", label: "Quarterly" },
  { value: "Yearly", label: "Yearly" },
];

const resolveAppraisalTypeOption = (
  rawValue,
  options = FALLBACK_APPRAISAL_TYPE_OPTIONS,
  designationNames = []
) => {
  if (rawValue == null || rawValue === "") return null;
  const normalized = String(rawValue).trim().toLowerCase();
  const names = (designationNames || [])
    .map((name) => String(name || "").trim().toLowerCase())
    .filter(Boolean);

  const byId = options.find(
    (option) =>
      String(option.value || "").toLowerCase() === normalized ||
      String(option.id || "").toLowerCase() === normalized
  );
  if (byId) return byId;

  const byName = options.find(
    (option) =>
      String(option.label || "").toLowerCase() === normalized ||
      String(option.name || "").toLowerCase() === normalized
  );
  if (byName) return byName;

  const codeKey = normalized === "agents" ? "agent" : normalized;
  const byCode = options.filter(
    (option) => String(option.code || option.value || "").toLowerCase() === codeKey
  );
  if (byCode.length === 1) return byCode[0];
  if (byCode.length > 1) {
    const byDesignation = byCode.find((option) =>
      names.includes(String(option.label || option.name || "").toLowerCase())
    );
    if (byDesignation) return byDesignation;
  }

  return byCode[0] || { value: String(rawValue), label: String(rawValue) };
};

const resolveAppraisalFrequencyOption = (rawValue) => {
  if (rawValue == null || rawValue === "") return null;
  const normalized = String(rawValue).trim().toLowerCase();
  const match = APPRAISAL_FREQUENCY_OPTIONS.find(
    (option) =>
      option.value.toLowerCase() === normalized ||
      option.label.toLowerCase() === normalized
  );
  if (match) return match;
  return { value: String(rawValue), label: String(rawValue) };
};
const parseWeightage = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const buildSectionWeightErrors = (sections = [], sectionWeightages = {}) => {
  const perSection = {};
  let total = 0;

  sections.forEach((sectionId) => {
    const parsedWeightage = parseWeightage(sectionWeightages[sectionId]);
    if (parsedWeightage === null) {
      perSection[sectionId] = "Weightage is required";
      return;
    }
    if (parsedWeightage <= 0 || parsedWeightage > 100) {
      perSection[sectionId] = "Weightage must be greater than 0 and less than or equal to 100";
      return;
    }
    total += parsedWeightage;
  });

  return {
    perSection,
    total,
    totalError: sections.length > 0 && total !== 100
      ? "Total section weightage must be exactly 100"
      : null,
  };
};

const mapCampaignsToSelectValue = (campaigns) => {
  if (isEmpty(campaigns)) return [];
  const seen = new Set();
  return Object.values(campaigns)
    .filter((c) => c && c.id)
    .filter((c) => {
      const key = String(c.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((c) => ({
      value: c.id,
      label: c.client_name || c.name || String(c.id),
    }));
};

const mapDesignationsToSelectValue = (designations) => {
  if (isEmpty(designations)) return [];
  const seen = new Set();
  return Object.values(designations)
    .filter((d) => d && d.id)
    .filter((d) => {
      const key = String(d.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((d) => ({
      value: d.id,
      label: d.name || d.designation_name || String(d.id),
    }));
};

const dedupeIds = (ids = []) => {
  const seen = new Set();
  return ids.filter((id) => {
    if (id == null || id === "") return false;
    const key = String(id);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const mapOfficeValueFromRecord = (record) => {
  if (!record) return { office_id: null, officeValue: null };
  const id = record.office_id ?? record.office?.id;
  if (id == null || id === "") return { office_id: null, officeValue: null };
  const label =
    record.office?.name ??
    record.office?.title ??
    record.office_name ??
    String(id);
  return { office_id: id, officeValue: { value: id, label } };
};

/** Reads section ids/weights from `setup_kpis`, `setup_sections`, or `sections` relation. */
const extractSectionsFromAppraisalRecord = (appraisalFormObj) => {
  let sectionIds = [];
  let sectionWeights = {};
  const sectionPivotRowIds = {};

  const pivotRows = getAppraisalSetupSectionPivotRows(appraisalFormObj);
  if (pivotRows.length > 0) {
    pivotRows.forEach((row) => {
      if (!row) return;
      const sid =
        row.section_id ??
        row.section?.id ??
        row[F.fkId] ??
        row.pivot?.section_id ??
        row.pivot?.[F.fkId];
      if (!sid) return;
      if (sectionIds.some((id) => String(id) === String(sid))) return;
      sectionIds.push(sid);
      sectionWeights[sid] = row.weightage ?? row.pivot?.weightage ?? "";
      if (row.id) sectionPivotRowIds[sid] = row.id;
    });
    return { sectionIds, sectionWeights, sectionPivotRowIds };
  }

  if (!isEmpty(appraisalFormObj.sections)) {
    const rows = Array.isArray(appraisalFormObj.sections)
      ? appraisalFormObj.sections
      : Object.values(appraisalFormObj.sections);
    rows.forEach((row) => {
      if (!row) return;
      const sid =
        row.id ?? row.section_id ?? row[F.fkId] ?? row.pivot?.section_id ?? row.pivot?.[F.fkId];
      if (!sid) return;
      if (sectionIds.some((id) => String(id) === String(sid))) return;
      sectionIds.push(sid);
      sectionWeights[sid] = row.pivot?.weightage ?? row.weightage ?? "";
    });
  }

  return { sectionIds, sectionWeights, sectionPivotRowIds };
};

const validationSchema = {
  appraisal_type: {
    presence: { allowEmpty: false, message: "^Appraisal Type is required" },
  },
  frequency: {
    presence: { allowEmpty: false, message: "^Appraisal Frequency is required" },
  },
  title: {
    presence: { allowEmpty: false, message: "^Title is required" },
    length: {
      minimum: 3,
      maximum: 100,
      message: "^Title must be between 3 and 100 characters",
    },
  },
  office_id: {
    presence: { allowEmpty: false, message: "^Office is required" },
  },
  campaigns: {
    presence: { allowEmpty: false, message: "^At least one campaign is required" },
  },
  // Designations are optional - form can be submitted with or without designations
  sections: {
    presence: { allowEmpty: false, message: "^At least one Section is required" },
  },
};

const AppraisalSetupBasicInfoForm = () => { 

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const appraisalState = useSelector((state) => state.appraisalState);
  const session = useSelector((state) => state.session);
  const employeeState = useSelector((state) => state.employeeState);

  const [formState, setFormState] = useState({
    isValid: false,
    values: {
      appraisal_type: "",
      appraisal_typeValue: null,
      title: "",
      office_id: null,
      officeValue: null,
      campaigns: [],
      designations: [],
      sections: [],
      sectionWeightages: {},
      /** setup_section pivot row id per section_id (updates). */
      sectionPivotRowIds: {},
      frequency: "Quarterly",
      frequencyValue: { value: "Quarterly", label: "Quarterly" },
      description: "",
      object_viewed_id: (session?.current_page_permissions && !Array.isArray(session.current_page_permissions)) 
        ? session.current_page_permissions.object_id 
        : null,
    },
    touched: {
      object_viewed_id: true,
      frequency: true,
    },
    errors: {},
  });

  const [isUpdatingPublishStatus, setIsUpdatingPublishStatus] = useState(false);
  const [appraisalTypeOptions, setAppraisalTypeOptions] = useState(
    FALLBACK_APPRAISAL_TYPE_OPTIONS
  );

  useEffect(() => {
    const objectId =
      (session?.current_page_permissions &&
        !Array.isArray(session.current_page_permissions) &&
        session.current_page_permissions.object_id) ||
      null;
    if (!objectId) return;

    let cancelled = false;
    fetchAppraisalTypeOptions(objectId)
      .then((options) => {
        if (cancelled || !options?.length) return;
        setAppraisalTypeOptions(options);
      })
      .catch(() => {
        // Keep fallback options when the master list is unavailable.
      });

    return () => {
      cancelled = true;
    };
  }, [session?.current_page_permissions]);

  // Update object_viewed_id when session permissions are set
  useEffect(() => {
    if (session?.current_page_permissions && !Array.isArray(session.current_page_permissions)) {
      const objectId = session.current_page_permissions.object_id;
      if (objectId && (!formState.values.object_viewed_id || formState.values.object_viewed_id === null)) {
        setFormState((prev) => ({
          ...prev,
          values: {
            ...prev.values,
            object_viewed_id: objectId
          }
        }));
      }
    }
  }, [session?.current_page_permissions]);

  // When validation errors come from server
  useEffect(() => { 
    if (!isEmpty(appraisalState.validation_error)) { 
      setFormState((prev) => ({
        ...prev,
        isValid: false,
        errors: appraisalState.validation_error
      }));
    }
  }, [appraisalState.validation_error]);

  // Auto-update campaigns when list loads and "all" was previously selected
  useEffect(() => {
    const campaignsList = employeeState?.campaignsList || [];
    const hasAllSelected = formState.values.campaignsValue?.some(item => item?.value === 'all');
    
    if (hasAllSelected && campaignsList.length > 0 && formState.values.campaigns.length === 0) {
      const allCampaignIds = campaignsList
        .filter(campaign => campaign && campaign.id)
        .map(campaign => campaign.id);
      if (allCampaignIds.length > 0) {
        setFormState((prev) => ({
          ...prev,
          values: { 
            ...prev.values, 
            campaigns: allCampaignIds 
          },
          touched: { ...prev.touched, campaigns: true },
        }));
      }
    }
  }, [employeeState?.campaignsList, formState.values.campaignsValue, formState.values.campaigns]);

  // Auto-update designations when list loads and "all" was previously selected
  useEffect(() => {
    const designationsList = employeeState?.designationsList || [];
    const hasAllSelected = formState.values.designationsValue?.some(item => item?.value === 'all');
    
    if (hasAllSelected && designationsList.length > 0 && formState.values.designations.length === 0) {
      const allDesignationIds = designationsList
        .filter(designation => designation && designation.id)
        .map(designation => designation.id);
      if (allDesignationIds.length > 0) {
        setFormState((prev) => ({
          ...prev,
          values: { 
            ...prev.values, 
            designations: allDesignationIds 
          },
          touched: { ...prev.touched, designations: true },
        }));
      }
    }
  }, [employeeState?.designationsList, formState.values.designationsValue, formState.values.designations]);

  useEffect(() => {
    const errors = validate(
      {
        ...formState.values,
        campaigns: formState.values.campaigns.length ? formState.values.campaigns : undefined,
        // Designations are optional - don't include in validation if empty
        sections: formState.values.sections.length ? formState.values.sections : undefined,
        office_id: formState.values.office_id ? formState.values.office_id : undefined,
      },
      validationSchema
    );
    const sectionWeightValidation = buildSectionWeightErrors(formState.values.sections, formState.values.sectionWeightages);
    const hasSectionWeightErrors = Object.keys(sectionWeightValidation.perSection).length > 0 || !!sectionWeightValidation.totalError;
    const nextIsValid = !errors && !hasSectionWeightErrors;
    const nextErrors = errors || {};

    setFormState((prev) => {
      const sameValid = prev.isValid === nextIsValid;
      const sameErrors =
        JSON.stringify(prev.errors || {}) === JSON.stringify(nextErrors);
      if (sameValid && sameErrors) {
        return prev;
      }
      return {
        ...prev,
        isValid: nextIsValid,
        errors: nextErrors,
      };
    });
  }, [formState.values]);

  // Populate form from loaded record only. Never navigate here — that races sidebar routing
  // when setup flags are cleared while leaving this page.
  useEffect(() => {
    const isUpdatePath = location.pathname === "/appraisalSetup/update";
    const isViewPath = location.pathname === "/appraisalSetup/view";
    const shouldLoadUpdate =
      isUpdatePath &&
      appraisalState.showUpdateForm &&
      !isEmpty(appraisalState.appraisalRecord);
    const shouldLoadView =
      isViewPath &&
      appraisalState.showViewPage &&
      !isEmpty(appraisalState.appraisalRecord);

    if (!shouldLoadUpdate && !shouldLoadView) {
      return;
    }

    const appraisalFormObj = appraisalState.appraisalRecord;
    let campaigns_ids = [];
    let designations_ids = [];
    if (!isEmpty(appraisalFormObj.campaigns)) {
      campaigns_ids = dedupeIds(
        Object.values(appraisalFormObj.campaigns)
          .filter((campaign) => campaign && campaign.id)
          .map((campaign) => campaign.id)
      );
    }
    if (!isEmpty(appraisalFormObj.designations)) {
      designations_ids = dedupeIds(
        Object.values(appraisalFormObj.designations)
          .filter((designation) => designation && designation.id)
          .map((designation) => designation.id)
      );
    }
    const {
      sectionIds,
      sectionWeights,
      sectionPivotRowIds,
    } = extractSectionsFromAppraisalRecord(appraisalFormObj);

    const designationNames = (appraisalFormObj.designations || [])
      .map((designation) => designation?.name || designation?.designation_name || "")
      .filter(Boolean);
    const appraisalTypeOption = resolveAppraisalTypeOption(
      appraisalFormObj.appraisal_type ??
        appraisalFormObj.appraisal_types ??
        appraisalFormObj.paf_type,
      appraisalTypeOptions,
      designationNames
    );
    const frequencyOption = resolveAppraisalFrequencyOption(
      appraisalFormObj.frequency || "Quarterly"
    );

    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        id: appraisalFormObj.id,
        appraisal_type: appraisalTypeOption?.value || "",
        appraisal_typeValue: appraisalTypeOption,
        title: appraisalFormObj.title,
        ...mapOfficeValueFromRecord(appraisalFormObj),
        campaigns: campaigns_ids,
        campaignsValue: mapCampaignsToSelectValue(appraisalFormObj.campaigns),
        designations: designations_ids,
        designationsValue: mapDesignationsToSelectValue(appraisalFormObj.designations),
        sections: sectionIds,
        sectionWeightages: sectionWeights,
        sectionPivotRowIds: sectionPivotRowIds ?? {},
        frequency: frequencyOption?.value || "Quarterly",
        frequencyValue: frequencyOption,
        description: appraisalFormObj.description,
        object_viewed_id:
          prev.values.object_viewed_id ||
          session.current_page_permissions?.object_id,
      },
      touched: {
        ...prev.touched,
        appraisal_type: true,
        title: true,
        office_id: true,
        campaigns: true,
        designations: true,
        sections: true,
        frequency: true,
        description: true,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appraisalState.showUpdateForm,
    appraisalState.showViewPage,
    appraisalState.appraisalRecord,
    appraisalTypeOptions,
    location.pathname,
  ]);

  const handleChange = (e) => {
    e.persist();
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [e.target.name]: e.target.value,
      },
      touched: {
        ...prev.touched,
        [e.target.name]: true,
      },
    }));
    dispatch(hideAppraisalsValidationError(e.target.name));
  };

  const handleOfficeChange = (selected) => {
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        office_id: selected?.value ?? null,
        officeValue: selected ?? null,
      },
      touched: { ...prev.touched, office_id: true },
    }));
    dispatch(hideAppraisalsValidationError("office_id"));
  };

  const handleAppraisalTypeChange = (selected) => {
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        appraisal_type: selected?.value ?? "",
        appraisal_typeValue: selected ?? null,
      },
      touched: { ...prev.touched, appraisal_type: true },
    }));
    dispatch(hideAppraisalsValidationError("appraisal_type"));
  };

  const handleEditorChange = (data) => {
    if ((formState.values.description || "") === (data || "")) {
      return;
    }
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        description: data,
      },
      touched: { ...prev.touched, description: true },
    }));
    dispatch(hideAppraisalsValidationError("description"));
  };

  const handleCampaignsChange = (selected) => {
    if (!selected || selected.length === 0) {
      setFormState((prev) => ({
        ...prev,
        values: { ...prev.values, campaigns: [], campaignsValue: [] },
        touched: { ...prev.touched, campaigns: true },
      }));
      dispatch(hideAppraisalsValidationError("campaigns"));
      return;
    }

    const hasAll = selected.some((item) => item && item.value === "all");
    let campaignIds = [];
    let campaignsValue = selected;
    let shouldMarkAsTouched = true;

    if (hasAll) {
      const campaignsList = employeeState?.campaignsList || [];
      const allOptions = campaignsList
        .filter((campaign) => campaign && campaign.id)
        .map((campaign) => ({
          value: campaign.id,
          label: campaign.name || campaign.client_name || String(campaign.id),
        }));
      const realSelected = selected.filter((item) => item && item.value !== "all");
      const allAlreadySelected =
        allOptions.length > 0 &&
        allOptions.every((opt) =>
          realSelected.some((item) => String(item.value) === String(opt.value))
        );

      if (allAlreadySelected) {
        // Clicking "All" again when everything is selected clears the selection
        campaignIds = [];
        campaignsValue = [];
      } else if (allOptions.length > 0) {
        campaignIds = allOptions.map((opt) => opt.value);
        campaignsValue = allOptions;
      } else {
        campaignIds = [];
        campaignsValue = [];
        shouldMarkAsTouched = false;
      }
    } else {
      campaignIds = dedupeIds(selected.filter((item) => item && item.value).map((item) => item.value));
      const seen = new Set();
      campaignsValue = selected.filter((item) => {
        if (!item || item.value == null) return false;
        const key = String(item.value);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    setFormState((prev) => ({
      ...prev,
      values: { ...prev.values, campaigns: campaignIds, campaignsValue },
      touched: {
        ...prev.touched,
        campaigns: shouldMarkAsTouched ? true : prev.touched.campaigns,
      },
    }));
    dispatch(hideAppraisalsValidationError("campaigns"));
  };

  const handleDesignationChange = (selected) => {
    if (!selected || selected.length === 0) {
      setFormState((prev) => ({
        ...prev,
        values: { ...prev.values, designations: [], designationsValue: [] },
        touched: { ...prev.touched, designations: true },
      }));
      dispatch(hideAppraisalsValidationError("designations"));
      return;
    }

    const hasAll = selected.some((item) => item && item.value === "all");
    let designationIds = [];
    let designationsValue = selected;
    let shouldMarkAsTouched = true;

    if (hasAll) {
      const designationsList = employeeState?.designationsList || [];
      const allOptions = designationsList
        .filter((designation) => designation && designation.id)
        .map((designation) => ({
          value: designation.id,
          label: designation.name || designation.designation_name || String(designation.id),
        }));
      const realSelected = selected.filter((item) => item && item.value !== "all");
      const allAlreadySelected =
        allOptions.length > 0 &&
        allOptions.every((opt) =>
          realSelected.some((item) => String(item.value) === String(opt.value))
        );

      if (allAlreadySelected) {
        // Clicking "All" again when everything is selected clears the selection
        designationIds = [];
        designationsValue = [];
      } else if (allOptions.length > 0) {
        designationIds = allOptions.map((opt) => opt.value);
        designationsValue = allOptions;
      } else {
        designationIds = [];
        designationsValue = [];
        shouldMarkAsTouched = false;
      }
    } else {
      designationIds = dedupeIds(
        selected.filter((item) => item && item.value).map((item) => item.value)
      );
      const seen = new Set();
      designationsValue = selected.filter((item) => {
        if (!item || item.value == null) return false;
        const key = String(item.value);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    setFormState((prev) => ({
      ...prev,
      values: { ...prev.values, designations: designationIds, designationsValue },
      touched: {
        ...prev.touched,
        designations: shouldMarkAsTouched ? true : prev.touched.designations,
      },
    }));
    dispatch(hideAppraisalsValidationError("designations"));
  };

  const handleSectionChange = (selected) => {
    const rawIds = selected ? selected.map((item) => item.value) : [];
    const sectionIds = dedupeIds(rawIds);
    const existingWeightages = formState.values.sectionWeightages || {};
    const existingPivotIds = formState.values.sectionPivotRowIds || {};
    const updatedWeightages = {};
    const updatedPivotIds = {};
    sectionIds.forEach((sectionId) => {
      updatedWeightages[sectionId] = existingWeightages[sectionId] ?? "";
      if (existingPivotIds[sectionId]) updatedPivotIds[sectionId] = existingPivotIds[sectionId];
    });
    const seen = new Set();
    const sectionsValue = (selected || []).filter((item) => {
      if (!item || item.value == null) return false;
      const key = String(item.value);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        sections: sectionIds,
        sectionsValue,
        sectionWeightages: updatedWeightages,
        sectionPivotRowIds: updatedPivotIds,
      },
      touched: { ...prev.touched, sections: true, sectionWeightages: true },
    }));
    dispatch(hideAppraisalsValidationError("sections"));
  };

  const handleSectionWeightageChange = (sectionId, value) => {
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        sectionWeightages: {
          ...(prev.values.sectionWeightages || {}),
          [sectionId]: value,
        },
      },
      touched: {
        ...prev.touched,
        sectionWeightages: true,
      },
    }));
    dispatch(hideAppraisalsValidationError("sections"));
  };

  const handleFrequencyChange = (selected) => {
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        frequency: selected?.value ?? "",
        frequencyValue: selected ?? null,
      },
      touched: { ...prev.touched, frequency: true },
    }));
    dispatch(hideAppraisalsValidationError("frequency"));
  };

  const hasError = (field) => {
    return formState.touched[field] && formState.errors[field] ? true : false;
  };

  // Cancel uses flushSync navigate so RR commits before CKEditor teardown
  // can abort an async transition (same pattern as sidebar NavigationListItem).
  const handleCancel = (e) => {
    if (e) {
      e.preventDefault();
    }
    flushSync(() => {
      navigate("/appraisalSetup", { replace: true });
    });
    dispatch(resetAppraisalSetupFlow());
  };

  const isViewMode = location.pathname === '/appraisalSetup/view';
  const isUpdateMode = location.pathname === '/appraisalSetup/update';
  /** Published setups cannot change type, frequency, or targeting; drafts stay editable */
  const isPublishedAppraisal = Number(appraisalState?.appraisalRecord?.is_published) === 1;
  const lockCampaignDesignationSection =
    isViewMode || (isUpdateMode && isPublishedAppraisal);
  const appraisalSetupId = appraisalState?.appraisalRecord?.id || formState.values.id;

  const handlePublishStatusChange = async (status) => {
    const nextValue = status === PUBLISH_STATUS.PUBLISHED ? 1 : 0;
    if (!appraisalSetupId || nextValue === Number(appraisalState?.appraisalRecord?.is_published)) {
      return;
    }

    const object_viewed_id =
      formState.values.object_viewed_id ||
      session?.current_page_permissions?.object_id ||
      session?.current_object_viewed_id ||
      '';

    setIsUpdatingPublishStatus(true);
    try {
      await dispatch(updateAppraisalSetupPublishStatus(appraisalSetupId, nextValue, object_viewed_id));
    } catch (error) {
      console.error('Error updating appraisal publish status:', error);
    } finally {
      setIsUpdatingPublishStatus(false);
    }
  };

  // Helper setters so dropdown components can populate initial selected values
  const setCampaignDropdownValue = (selected) => {
    setFormState((prev) => {
      const next = selected || [];
      if (isEqual(prev.values.campaignsValue, next)) {
        return prev;
      }
      return {
        ...prev,
        values: {
          ...prev.values,
          campaignsValue: next,
        },
      };
    });
  };

  const setDesignationDropdownValue = (selected) => {
    setFormState((prev) => {
      const next = selected || [];
      if (isEqual(prev.values.designationsValue, next)) {
        return prev;
      }
      return {
        ...prev,
        values: {
          ...prev.values,
          designationsValue: next,
        },
      };
    });
  };

  const setSectionDropdownValue = (selected) => {
    setFormState((prev) => {
      const next = selected || [];
      if (isEqual(prev.values.sectionsValue, next)) {
        return prev;
      }
      return {
        ...prev,
        values: {
          ...prev.values,
          sectionsValue: next,
        },
      };
    });
  };

  const handleNext = async (e) => {
    // Prevent default form submission if button is inside a form
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const sectionWeightValidationRun = buildSectionWeightErrors(formState.values.sections, formState.values.sectionWeightages);

    if (!formState.isValid || Object.keys(sectionWeightValidationRun.perSection).length > 0 || sectionWeightValidationRun.totalError) {
      setFormState((prev) => ({
        ...prev,
        touched: {
          appraisal_type: true,
          frequency: true,
          appraisal_title: true,
          title: true,
          campaigns: true,
          office_id: true,
          sections: true,
          sectionWeightages: true,
        },
      }));
      return;
    }

    try {
      // Ensure object_viewed_id is always included from session - get fresh value at submission time
      let object_viewed_id = formState.values.object_viewed_id;
      
      // If not in form state, try to get from session (handle both array and object cases)
      if (!object_viewed_id || object_viewed_id === null) {
        if (session?.current_page_permissions && !Array.isArray(session.current_page_permissions)) {
          object_viewed_id = session.current_page_permissions.object_id;
        }
      }
      
      // If still null, try to get from appraisal list (from previous fetch)
      if (!object_viewed_id && appraisalState?.appraisalsList && appraisalState.appraisalsList.length > 0) {
        const firstAppraisal = appraisalState.appraisalsList[0];
        if (firstAppraisal?.object_viewed_id) {
          object_viewed_id = firstAppraisal.object_viewed_id;
        }
      }

      // Prepare campaigns - ensure we send an array of UUIDs (or empty array)
      let campaigns = formState.values.campaigns || [];
      
      // If "all" is still in the values (shouldn't happen, but just in case), handle it
      if (formState.values.campaignsValue?.some(item => item?.value === 'all')) {
        const campaignsList = employeeState?.campaignsList || [];
        if (campaignsList.length > 0) {
          campaigns = campaignsList
            .filter(campaign => campaign && campaign.id)
            .map(campaign => campaign.id);
        } else {
          campaigns = []; // Send empty array if no campaigns available
        }
      }

      // Prepare designations - ensure we send an array of UUIDs (or empty array)
      let designations = formState.values.designations || [];
      
      // If "all" is still in the values (shouldn't happen, but just in case), handle it
      if (formState.values.designationsValue?.some(item => item?.value === 'all')) {
        const designationsList = employeeState?.designationsList || [];
        if (designationsList.length > 0) {
          designations = designationsList
            .filter(designation => designation && designation.id)
            .map(designation => designation.id);
        } else {
          designations = []; // Send empty array if no designations available
        }
      }

      const pivotRowsForSetup = (formState.values.sections || []).map((sectionId) => {
        const row = {
          section_id: sectionId,
          [F.fkId]: sectionId,
          weightage: parseWeightage(formState.values.sectionWeightages?.[sectionId]),
        };
        const pivotRowId = formState.values.sectionPivotRowIds?.[sectionId];
        if (pivotRowId) row.id = pivotRowId;
        return row;
      });

      const formData = {
        ...formState.values,
        campaigns: campaigns, // Ensure we always send an array
        designations: designations, // Ensure we always send an array
        [F.setupPivotRows]: pivotRowsForSetup,
        object_viewed_id: object_viewed_id
      };

      let resMethod = appraisalState.showUpdateForm ? "PUT" : "POST";
      await dispatch(createOrUpdateAppraisal(formData, resMethod));

      /*navigate("/setupCategory", {
        state: { selectedSections: formState.values.sections, editorData: formState.values.description },
      });*/
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const sectionWeightValidation = buildSectionWeightErrors(formState.values.sections, formState.values.sectionWeightages);
  const selectedSectionOptions = formState.values.sectionsValue || [];
  const shouldShowSectionWeightErrors = !!(formState.touched.sections || formState.touched.sectionWeightages);

  return (
    <>
      <div>
        <PageHeader title="Appraisal Setup Form">
          {(isUpdateMode || isViewMode) && appraisalSetupId && (
            <PublishStatusToggle
              id="appraisal-setup-publish-toggle"
              isPublished={isPublishedAppraisal}
              onChange={handlePublishStatusChange}
              loading={isUpdatingPublishStatus || appraisalState.loading}
              disabled={isViewMode}
            />
          )}
        </PageHeader>
        <div className="main-content-div page-card appraisal-setup-form">

          <Row className="g-3 appraisal-setup-form__row">
            <Col lg={3} md={6} xs={12}>
              <div className="appraisal-setup-form__field">
                <Form.Group>
                  <FormFieldLabel required>Title</FormFieldLabel>
                  <Form.Control
                    type="text"
                    name="title"
                    placeholder="Appraisal Title"
                    className={`input-field ${hasError("title") ? "is-invalid" : ""}`}
                    value={formState.values.title}
                    onChange={handleChange}
                    readOnly={isViewMode}
                    disabled={isViewMode}
                  />
                  {hasError("title") && (
                    <div className="invalid-feedback">{formState.errors.title[0]}</div>
                  )}
                </Form.Group>
              </div>
            </Col>

            <Col lg={3} md={6} xs={12}>
              <div className="appraisal-setup-form__field">
                <OfficeDropdown
                  officeValue={formState.values.officeValue}
                  officeOnChange={handleOfficeChange}
                  label="Office"
                  placeholder="Select office"
                  isDisabled={lockCampaignDesignationSection}
                  required
                />
                {hasError("office_id") && (
                  <div className="text-danger mt-1 font-13">{formState.errors.office_id[0]}</div>
                )}
              </div>
            </Col>

            <Col lg={3} md={6} xs={12}>
              <div className="appraisal-setup-form__field">
                <CampaignDropdown
                  selectedId={formState.values.campaigns}
                  campaignValue={formState.values.campaignsValue}
                  setCampaignValue={setCampaignDropdownValue}
                  campaignOnChange={handleCampaignsChange}
                  label="Campaigns"
                  placeholder="Select Campaigns"
                  isDisabled={lockCampaignDesignationSection}
                  required
                />
                {hasError("campaigns") && (
                  <div className="text-danger mt-1 font-13">{formState.errors.campaigns[0]}</div>
                )}
              </div>
            </Col>

            <Col lg={3} md={6} xs={12}>
              <div className="appraisal-setup-form__field">
                <DesignationDropdown
                  selectedId={formState.values.designations}
                  designationValue={formState.values.designationsValue}
                  setDesignationValue={setDesignationDropdownValue}
                  designationOnChange={handleDesignationChange}
                  label="Designations"
                  placeholder="Select Designations"
                  isDisabled={lockCampaignDesignationSection}
                />
                {hasError("designations") && (
                  <div className="text-danger mt-1 font-13">{formState.errors.designations[0]}</div>
                )}
              </div>
            </Col>
          </Row>

          <Row className="g-3 appraisal-setup-form__row">
            <Col lg={6} md={6} xs={12}>
              <div className="appraisal-setup-form__field">
                <FormFieldLabel required>Appraisal Type</FormFieldLabel>
                <Select
                  name="appraisal_type"
                  placeholder="Appraisal Type"
                  className={`multi-select w-100${hasError("appraisal_type") ? " is-invalid" : ""}`}
                  classNamePrefix="react-select"
                  options={appraisalTypeOptions}
                  value={formState.values.appraisal_typeValue}
                  onChange={handleAppraisalTypeChange}
                  isSearchable
                  isClearable
                  isDisabled={lockCampaignDesignationSection}
                  components={{ ValueContainer: CustomDropdownContainer }}
                />
                {hasError("appraisal_type") && (
                  <div className="text-danger mt-1 font-13">
                    {formState.errors.appraisal_type[0]}
                  </div>
                )}
              </div>
            </Col>
            <Col lg={6} md={6} xs={12}>
              <div className="appraisal-setup-form__field">
                <FormFieldLabel required>Appraisal Frequency</FormFieldLabel>
                <Select
                  name="frequency"
                  placeholder="Appraisal Frequency"
                  className={`multi-select w-100${hasError("frequency") ? " is-invalid" : ""}`}
                  classNamePrefix="react-select"
                  options={APPRAISAL_FREQUENCY_OPTIONS}
                  value={formState.values.frequencyValue}
                  onChange={handleFrequencyChange}
                  isSearchable
                  isClearable
                  isDisabled={lockCampaignDesignationSection}
                  components={{ ValueContainer: CustomDropdownContainer }}
                />
                {hasError("frequency") && (
                  <div className="text-danger mt-1 font-13">
                    {formState.errors.frequency[0]}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          <Row className="g-3 appraisal-setup-form__row">
            <Col lg={6} md={12} xs={12}>
              <div className="appraisal-setup-form__sections-block">
                <AppraisalSectionDropdown
                  selectedId={formState.values.sections}
                  sectionValue={formState.values.sectionsValue}
                  setSectionValue={setSectionDropdownValue}
                  sectionOnChange={handleSectionChange}
                  label="Sections"
                  placeholder="Choose / select Sections"
                  showSelectAllOption={false}
                  isDisabled={lockCampaignDesignationSection}
                  required
                />
                {hasError("sections") && (
                  <div className="text-danger mt-1 font-13">{formState.errors.sections[0]}</div>
                )}
              </div>
            </Col>

            {selectedSectionOptions.length > 0 && (
              <Col lg={6} md={12} xs={12}>
                <div className="appraisal-setup-form__weightage-panel">
                  <p className="appraisal-setup-form__weightage-title">Section Weightage</p>
                  <div className="appraisal-setup-form__weightage-grid">
                    {selectedSectionOptions.map((sectionOption) => {
                      const sectionId = sectionOption.value;
                      const perSectionError = sectionWeightValidation.perSection[sectionId];
                      return (
                        <Form.Group key={sectionId} className="appraisal-setup-form__weightage-item">
                          <Form.Label className="label-input">
                            <span className="appraisal-setup-form__weightage-name">
                              {sectionOption.label}
                            </span>
                            <span className="appraisal-setup-form__weightage-badge">
                              Weightage (%)
                            </span>
                          </Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            placeholder="Enter weightage"
                            value={formState.values.sectionWeightages?.[sectionId] ?? ""}
                            onChange={(event) => handleSectionWeightageChange(sectionId, event.target.value)}
                            readOnly={lockCampaignDesignationSection}
                            disabled={lockCampaignDesignationSection}
                            className={shouldShowSectionWeightErrors && perSectionError ? "is-invalid" : ""}
                          />
                          {shouldShowSectionWeightErrors && perSectionError && (
                            <div className="invalid-feedback d-block">{perSectionError}</div>
                          )}
                        </Form.Group>
                      );
                    })}
                  </div>
                  <div className="appraisal-setup-form__weightage-total">
                    <span>
                      Total Weightage: <strong>{sectionWeightValidation.total}</strong>/100
                    </span>
                    {shouldShowSectionWeightErrors && sectionWeightValidation.totalError && (
                      <div className="text-danger font-13">{sectionWeightValidation.totalError}</div>
                    )}
                  </div>
                </div>
              </Col>
            )}
          </Row>

          <Row className="g-3 appraisal-setup-form__row">
            <Col xs={12}>
              <div className="appraisal-setup-form__description">
                <Form.Label className="label-input">Description</Form.Label>
                <CkEditorField
                  data={formState.values.description}
                  readOnly={isViewMode}
                  onChange={handleEditorChange}
                />
              </div>
            </Col>
          </Row>

          <div className="appraisal-form-actions">
            <Link
              to="/appraisalSetup"
              replace
              className="c-b-btn text-decoration-none"
              onClick={handleCancel}
            >
              <img className="me-2" src="/assets/images/back-arrow.svg" alt="" aria-hidden="true" />
              {isViewMode ? "Back" : "Cancel"}
            </Link>
            {isViewMode && (
              <button
                type="button"
                className="n-u-btn"
                onClick={() => dispatch(toggleAppraisalSetupCategoryWindow(true))}
              >
                Next <img className="ms-2" src="/assets/images/next-icon.svg" alt="" aria-hidden="true" />
              </button>
            )}
            {!isViewMode && (
              <button type="button" className="n-u-btn" disabled={!formState.isValid} onClick={handleNext}>
                Next <img className="ms-2" src="/assets/images/next-icon.svg" alt="" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>

    </>
  );
};

const AppraisalForm = () => {
  const showCategorySetupWindow = useSelector(
    (state) => state.appraisalState.showCategorySetupWindow
  );

  if (showCategorySetupWindow) {
    return <SetupCaregory key="appraisal-setup-category" />;
  }

  return <AppraisalSetupBasicInfoForm key="appraisal-setup-basic" />;
};




export default AppraisalForm;
